import {
    Button,
    Grid,
    InputAdornment,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Toolbar,
    Typography,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { IoMdAdd } from "react-icons/io";
import CompanyAuth from "../../components/auth/CompanyAuth";
import { isAuth } from "../../actions/auth";
import Controls from "../../components/controls/Controls";
import Notify from "../../components/customHelpers/Notify";
import Popup from "../../components/customHelpers/Popup";
import JobForm from "../../components/forms/JobForm";
import Layout from "../../components/layout/Layout";
import Jobs from "../../actions/Jobs";
import useTable from "../../components/customHelpers/useTable";
import { MdOutlineDelete } from "react-icons/md";
import { FiEdit, FiEye, FiUsers } from "react-icons/fi";
import Loading from "../../components/customHelpers/Loading";
import Norecords from "../../components/customHelpers/Norecords";
import Notification from "../../components/customHelpers/Notification";
import ConfirmDialog from "../../components/customHelpers/ConfirmDialog";
import ViewJob from "../../components/ViewJob";

const headCells = [
    { id: "title", label: "Job Title" },
    { id: "status", label: "Status" },
    { id: "applicantCount", label: "Applicants" },
    { id: "createdAt", label: "Posted At" },
    { id: "actions", label: "Actions", disableSorting: true },
];

const formatApplicantStatus = (status) => {
    switch (status) {
        case "reviewing":
            return "Under Review";
        case "accepted":
            return "Accepted";
        case "rejected":
            return "Rejected";
        default:
            return "Submitted";
    }
};

const makeData = (result) => {
    return result.map((data) => {
        const statusRaw = data.status || (data.approved === true ? "approved" : "pending");
        const status = statusRaw === "pending" ? "Waiting for approval" : statusRaw;
        return {
            title: data.Title || data.title, // Handle capitalized key from backend
            Id: data.Id,
            body: data.Body || data.body,
            author: data.Author || data.author,
            createdAt: data.createdAt,
            status,
            applicantCount: Number(data.applicantCount || 0),
        };
    });
};

const CompanyJobs = () => {
    const {
        deleteCompanyJob,
        getUserJobs,
        getJobApplicants,
        getJobApplicantDetails,
        updateJobApplicantStatus,
        sendJobApplicantEmail,
        archiveJobApplicant,
    } = Jobs();
    const [openPopup, setOpenPopup] = useState(false);
    const [openPopupJob, setOpenPopupJob] = useState(false);
    const [jobToView, setJobToView] = useState(null);
    const [openApplicantsPopup, setOpenApplicantsPopup] = useState(false);
    const [openApplicantDetailsPopup, setOpenApplicantDetailsPopup] = useState(false);
    const [applicantsLoading, setApplicantsLoading] = useState(false);
    const [applicantDetailsLoading, setApplicantDetailsLoading] = useState(false);
    const [applicantActionLoading, setApplicantActionLoading] = useState(false);
    const [selectedJobApplicants, setSelectedJobApplicants] = useState([]);
    const [selectedJobId, setSelectedJobId] = useState("");
    const [selectedJobTitle, setSelectedJobTitle] = useState("");
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [applicantEmailDraft, setApplicantEmailDraft] = useState({
        subject: "",
        message: "",
    });
    const { NotifyMessage, notify, setNotify } = Notify();
    const [recordForEdit, setRecordForEdit] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: "",
        subTitle: "",
    });

    const [filterFn, setFilterFn] = useState({
        fn: (items) => {
            return items;
        },
    });
    const [values, setValues] = useState({
        loading: false,
        jobs: [],
        reload: false,
    });
    const { loading, jobs, reload } = values;

    const fetchCompanyJobs = () => {
        setValues((prev) => ({ ...prev, loading: true }));
        const user = isAuth();
        getUserJobs(user?.email).then((data) => {
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
                setValues((prev) => ({ ...prev, loading: false }));
            } else {
                setValues((prev) => ({
                    ...prev,
                    jobs: makeData(data.result || []),
                    loading: false,
                }));
            }
        });
    };

    useEffect(() => {
        fetchCompanyJobs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reload]);

    const handleSearch = (e) => {
        let target = e.target;
        setFilterFn({
            fn: (items) => {
                if (target.value === "") return items;
                return items.filter((x) =>
                    x.title.toLowerCase().includes(target.value.toLowerCase())
                );
            },
        });
    };

    const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
        useTable(
            jobs.sort((a, b) =>
                a.createdAt < b.createdAt ? 1 : b.createdAt < a.createdAt ? -1 : 0
            ),
            headCells,
            filterFn
        );

    const onDelete = (Id) => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        const user = isAuth();
        deleteCompanyJob(Id, user?.email).then((data) => {
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
            } else {
                NotifyMessage({ message: data.message, type: "success" });
                setValues((prev) => ({ ...prev, reload: !prev.reload }));
            }
        });
    };

    const onViewApplicants = (item) => {
        if (item.status !== "approved") {
            NotifyMessage({ message: "Applicants are available after the job is approved.", type: "info" });
            return;
        }

        setSelectedJobId(item.Id);
        setSelectedJobTitle(item.title || "Job");
        setSelectedJobApplicants([]);
        setApplicantsLoading(true);
        setOpenApplicantsPopup(true);

        getJobApplicants(item.Id).then((data) => {
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
                setSelectedJobApplicants([]);
            } else {
                setSelectedJobApplicants(data.applicants || []);
            }
            setApplicantsLoading(false);
        });
    };

    const refreshApplicants = (jobId = selectedJobId) => {
        if (!jobId) {
            return;
        }
        getJobApplicants(jobId).then((data) => {
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
                setSelectedJobApplicants([]);
            } else {
                setSelectedJobApplicants(data.applicants || []);
            }
        });
    };

    const onViewApplicantDetails = (applicant) => {
        setOpenApplicantDetailsPopup(true);
        setApplicantDetailsLoading(true);
        setSelectedApplicant(null);
        getJobApplicantDetails(applicant.Id).then((data) => {
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
                setOpenApplicantDetailsPopup(false);
            } else {
                setSelectedApplicant(data.applicant || null);
                setApplicantEmailDraft({
                    subject: "",
                    message: data.applicant?.companyMessage || "",
                });
                refreshApplicants();
                fetchCompanyJobs();
            }
            setApplicantDetailsLoading(false);
        });
    };

    const handleApplicantStatus = (status) => {
        if (!selectedApplicant) {
            return;
        }
        setApplicantActionLoading(true);
        updateJobApplicantStatus(selectedApplicant.Id, {
            status,
            emailSubject: applicantEmailDraft.subject,
            emailMessage: applicantEmailDraft.message,
            companyMessage: applicantEmailDraft.message,
        }).then((data) => {
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
            } else {
                setSelectedApplicant(data.applicant || selectedApplicant);
                NotifyMessage({ message: data.message, type: "success" });
                refreshApplicants();
            }
            setApplicantActionLoading(false);
        });
    };

    const handleApplicantEmail = () => {
        if (!selectedApplicant) {
            return;
        }
        if (!applicantEmailDraft.subject.trim() || !applicantEmailDraft.message.trim()) {
            NotifyMessage({ message: "Email subject and message are required.", type: "warning" });
            return;
        }
        setApplicantActionLoading(true);
        sendJobApplicantEmail(selectedApplicant.Id, applicantEmailDraft).then((data) => {
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
            } else {
                setSelectedApplicant(data.applicant || selectedApplicant);
                NotifyMessage({ message: data.message, type: "success" });
            }
            setApplicantActionLoading(false);
        });
    };

    const handleArchiveApplicant = () => {
        if (!selectedApplicant) {
            return;
        }
        setApplicantActionLoading(true);
        archiveJobApplicant(selectedApplicant.Id).then((data) => {
            if (data.err) {
                NotifyMessage({ message: data.err, type: "error" });
            } else {
                NotifyMessage({ message: data.message, type: "success" });
                setOpenApplicantDetailsPopup(false);
                refreshApplicants();
                fetchCompanyJobs();
            }
            setApplicantActionLoading(false);
        });
    };

    return (
        <Layout>
            <CompanyAuth>
                <div className="card">
                    <Typography variant="h6" className="page-header">
                        My Job Posts
                    </Typography>
                    <Toolbar>
                        <Controls.Input
                            label="Search My Jobs"
                            className="searchInput"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AiOutlineSearch />
                                    </InputAdornment>
                                ),
                            }}
                            onChange={handleSearch}
                        />
                        <Controls.Button
                            text="Create Job Post"
                            variant="outlined"
                            startIcon={<IoMdAdd />}
                            className="newButton"
                            onClick={() => {
                                setOpenPopup(true);
                                setRecordForEdit(null);
                            }}
                        />
                    </Toolbar>
                    <Grid container>
                        <Grid item xs={12} className="card__body">
                            {loading ? (
                                <Loading />
                            ) : (
                                <>
                                    <TblContainer>
                                        <TblHead />
                                        <TableBody>
                                            {recordsAfterPagingAndSorting().length > 0 ? (
                                                recordsAfterPagingAndSorting().map((item) => (
                                                    <TableRow key={item.Id}>
                                                        <TableCell>{item.title}</TableCell>
                                                        <TableCell>{item.status}</TableCell>
                                                        <TableCell>{item.applicantCount}</TableCell>
                                                        <TableCell>
                                                            {new Date(item.createdAt).toLocaleString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Controls.ActionButton
                                                                color="primary"
                                                                onClick={() => {
                                                                    setRecordForEdit({ ...item, editing: true });
                                                                    setOpenPopup(true);
                                                                }}
                                                            >
                                                                <FiEdit fontSize="medium" />
                                                            </Controls.ActionButton>
                                                            <Controls.ActionButton
                                                                color="secondary"
                                                                onClick={() => {
                                                                    setConfirmDialog({
                                                                        isOpen: true,
                                                                        title: "Delete job post?",
                                                                        subTitle: "This action cannot be undone.",
                                                                        onConfirm: () => onDelete(item.Id),
                                                                    });
                                                                }}
                                                            >
                                                                <MdOutlineDelete fontSize="medium" />
                                                            </Controls.ActionButton>
                                                            <Controls.ActionButton
                                                                color="primary"
                                                                onClick={() => {
                                                                    setJobToView(item);
                                                                    setOpenPopupJob(true);
                                                                }}
                                                            >
                                                                <FiEye fontSize="medium" />
                                                            </Controls.ActionButton>
                                                            <Controls.ActionButton
                                                                color="primary"
                                                                onClick={() => onViewApplicants(item)}
                                                            >
                                                                <FiUsers fontSize="medium" />
                                                            </Controls.ActionButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <Norecords col={5} />
                                            )}
                                        </TableBody>
                                    </TblContainer>
                                    <TblPagination />
                                </>
                            )}
                        </Grid>
                    </Grid>
                    <Popup
                        title="Job Post Form"
                        openPopup={openPopup}
                        setOpenPopup={setOpenPopup}
                    >
                        <JobForm
                            recordForEdit={recordForEdit}
                            NotifyMessage={NotifyMessage}
                            getAllJobs={fetchCompanyJobs}
                            setOpenPopup={setOpenPopup}
                            isCompany={true}
                            companyId={isAuth() ? isAuth().Id : null}
                        />
                    </Popup>
                    <Popup
                        title="Job Post Preview"
                        openPopup={openPopupJob}
                        setOpenPopup={setOpenPopupJob}
                        scroll="body"
                    >
                        {jobToView && <ViewJob jobToView={jobToView} />}
                    </Popup>
                    <Popup
                        title={`Applicants - ${selectedJobTitle}`}
                        openPopup={openApplicantsPopup}
                        setOpenPopup={setOpenApplicantsPopup}
                        scroll="body"
                    >
                        {applicantsLoading ? (
                            <Loading />
                        ) : selectedJobApplicants.length > 0 ? (
                            <div style={{ display: "grid", gap: 12 }}>
                                {selectedJobApplicants.map((applicant) => (
                                    <div
                                        key={applicant.Id}
                                        style={{
                                            border: "1px solid #e2e8f0",
                                            borderRadius: 10,
                                            padding: "0.85rem",
                                        }}
                                    >
                                        <Typography variant="subtitle2">
                                            {applicant.fullName}
                                        </Typography>
                                        <Typography variant="body2">
                                            Status: {formatApplicantStatus(applicant.applicationStatus)}
                                        </Typography>
                                        <Typography variant="body2">
                                            Email: {applicant.email}
                                        </Typography>
                                        <Typography variant="body2">
                                            Phone: {applicant.phone || "-"}
                                        </Typography>
                                        <Typography variant="body2">
                                            Role: {applicant.roleTitle || "-"}
                                        </Typography>
                                        <Typography variant="body2">
                                            Applied: {new Date(applicant.createdAt).toLocaleString()}
                                        </Typography>
                                        <div style={{ marginTop: 10 }}>
                                            <Button
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                onClick={() => onViewApplicantDetails(applicant)}
                                            >
                                                View Details
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                No applicants yet for this job.
                            </Typography>
                        )}
                    </Popup>
                    <Popup
                        title={selectedApplicant ? `Applicant - ${selectedApplicant.fullName}` : "Applicant Details"}
                        openPopup={openApplicantDetailsPopup}
                        setOpenPopup={setOpenApplicantDetailsPopup}
                        scroll="body"
                    >
                        {applicantDetailsLoading ? (
                            <Loading />
                        ) : selectedApplicant ? (
                            <div style={{ display: "grid", gap: 14 }}>
                                <div style={{ display: "grid", gap: 6 }}>
                                    <Typography variant="subtitle2">Application Status</Typography>
                                    <Typography variant="body2">{formatApplicantStatus(selectedApplicant.applicationStatus)}</Typography>
                                    <Typography variant="body2">Full Name: {selectedApplicant.fullName}</Typography>
                                    <Typography variant="body2">Email: {selectedApplicant.email}</Typography>
                                    <Typography variant="body2">Phone: {selectedApplicant.phone || "-"}</Typography>
                                    <Typography variant="body2">Current Company: {selectedApplicant.currentCompany || "-"}</Typography>
                                    <Typography variant="body2">Employment Type: {selectedApplicant.employmentType || "-"}</Typography>
                                    <Typography variant="body2">Country: {selectedApplicant.workCountry || "-"}</Typography>
                                    <Typography variant="body2">City: {selectedApplicant.workCity || "-"}</Typography>
                                    <Typography variant="body2">Work Mode: {selectedApplicant.workMode || "-"}</Typography>
                                    <Typography variant="body2">Work Period: {selectedApplicant.workPeriod || "-"}</Typography>
                                    <Typography variant="body2">Role: {selectedApplicant.roleTitle || "-"}</Typography>
                                    <Typography variant="body2">Education: {selectedApplicant.education || "-"}</Typography>
                                    <Typography variant="body2">License / Certification: {selectedApplicant.licensesAndCertifications || "-"}</Typography>
                                    <Typography variant="body2">Skills: {selectedApplicant.skills || "-"}</Typography>
                                    <Typography variant="body2">Applied: {new Date(selectedApplicant.createdAt).toLocaleString()}</Typography>
                                    <Typography variant="body2">Reviewed: {selectedApplicant.reviewedAt ? new Date(selectedApplicant.reviewedAt).toLocaleString() : "-"}</Typography>
                                </div>

                                <TextField
                                    label="Email Subject"
                                    variant="outlined"
                                    fullWidth
                                    value={applicantEmailDraft.subject}
                                    onChange={(event) => setApplicantEmailDraft((prev) => ({ ...prev, subject: event.target.value }))}
                                />
                                <TextField
                                    label="Email Message"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    value={applicantEmailDraft.message}
                                    onChange={(event) => setApplicantEmailDraft((prev) => ({ ...prev, message: event.target.value }))}
                                />

                                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        disabled={applicantActionLoading}
                                        onClick={() => handleApplicantStatus("accepted")}
                                    >
                                        Accept
                                    </Button>
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        disabled={applicantActionLoading}
                                        onClick={() => handleApplicantStatus("rejected")}
                                    >
                                        Reject
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        disabled={applicantActionLoading}
                                        onClick={handleApplicantEmail}
                                    >
                                        Send Email
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        disabled={applicantActionLoading || selectedApplicant.applicationStatus !== "rejected"}
                                        onClick={handleArchiveApplicant}
                                    >
                                        Delete From Dashboard
                                    </Button>
                                </div>
                                {selectedApplicant.applicationStatus !== "rejected" ? (
                                    <Typography variant="caption" color="textSecondary">
                                        Delete From Dashboard is enabled only after rejection.
                                    </Typography>
                                ) : null}
                            </div>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                Applicant not found.
                            </Typography>
                        )}
                    </Popup>
                    <Notification notify={notify} setNotify={setNotify} />
                    <ConfirmDialog
                        confirmDialog={confirmDialog}
                        setConfirmDialog={setConfirmDialog}
                    />
                </div>
            </CompanyAuth>
        </Layout>
    );
};

export default CompanyJobs;
